/**
 * Enhanced Product – Cart & App-Only Pricing
 *
 * Handles add-to-cart logic, button loading states, and app-only mode pricing.
 *
 * Depends on (set by enhanced-product-information.liquid inline script):
 *   window.enhancedIsProductPage  — boolean
 *   window.enhancedProductData    — product/pricing data object (null on non-product pages)
 *   window.selectedVariantId      — currently-selected variant ID
 *   window.buttonTranslations     — locale → translation map (from enhanced-product-helpers.js)
 *   window.customerData           — customer session info
 *   window.handleEnhancedProductAddToCart — DMO add-to-cart handler
 *   window.openSignupModal        — sign-up modal opener
 *   getCurrentLocale()            — locale helper (from enhanced-product-helpers.js)
 */

// ---------------------------------------------------------------------------
// Button loading state helpers
// ---------------------------------------------------------------------------

function showButtonLoading(button) {
  button.disabled = true;
  const spinner = button.querySelector('.button-spinner');
  const content = button.querySelector('.button-content');
  if (spinner) spinner.style.display = 'inline-flex';
  if (content) content.style.display = 'none';
}

function hideButtonLoading(button) {
  button.disabled = false;
  const spinner = button.querySelector('.button-spinner');
  const content = button.querySelector('.button-content');
  if (spinner) spinner.style.display = 'none';
  if (content) content.style.display = 'inline';
}

// ---------------------------------------------------------------------------
// Add-to-cart handler
// ---------------------------------------------------------------------------

function setupCustomAddToCartHandlers() {
  const enhancedSection = document.querySelector('.enhanced-product-section');
  if (!enhancedSection) return;

  const dataAddToCartBtn = enhancedSection.querySelector('[data-product-add-to-cart]');
  if (!dataAddToCartBtn) return;

  dataAddToCartBtn.addEventListener('click', function (e) {
    e.preventDefault();

    // Hide any existing error banner
    const errorBanner = enhancedSection.querySelector('[data-add-to-cart-error]');
    if (errorBanner) errorBanner.style.display = 'none';

    showButtonLoading(dataAddToCartBtn);

    const quantity = enhancedSection.querySelector('#quantity')?.value || 1;
    const isSubscription = enhancedSection.querySelector('#subscribe')?.checked;
    const deliverySelect = enhancedSection.querySelector('#delivery-select');
    const deliveryFrequency = deliverySelect?.value || 30;
    const selectedOption = deliverySelect?.options[deliverySelect.selectedIndex];
    const sellingPlanId = selectedOption?.getAttribute('data-selling-plan-id') || selectedOption?.value;

    const addToCartData = {
      quantity,
      subscription: isSubscription,
      deliveryFrequency,
      sellingPlanId: isSubscription ? sellingPlanId : null,
    };

    // Populate product-page-specific fields from window.enhancedProductData
    if (window.enhancedIsProductPage && window.enhancedProductData) {
      const pd = window.enhancedProductData;
      addToCartData.productId = pd.productId;
      addToCartData.variantId = window.selectedVariantId || pd.defaultVariantId;
      addToCartData.productHandle = pd.productHandle;
      addToCartData.hasSellingPlans = pd.hasSellingPlans;

      if (pd.hasSellingPlans) {
        addToCartData.price = isSubscription ? pd.subscriptionPrice : pd.basePrice;
        if (!addToCartData.sellingPlanId && isSubscription) {
          addToCartData.sellingPlanId = pd.defaultSellingPlanId;
        }
      } else {
        addToCartData.price = pd.basePrice;
        addToCartData.sellingPlanId = null;
      }
    }

    // Fallback timeout in case the DMO handler never settles
    const fallbackTimeout = setTimeout(() => {
      hideButtonLoading(dataAddToCartBtn);
    }, 10000);

    // App-only mode: gate behind sign-up if not logged in
    const isAppOnlyMode = enhancedSection.getAttribute('data-app-only-mode') === 'true';
    const isLoggedIn = window.customerData?.logged_in === true;

    if (isAppOnlyMode && !isLoggedIn) {
      hideButtonLoading(dataAddToCartBtn);
      clearTimeout(fallbackTimeout);

      if (typeof window.openSignupModal === 'function') {
        window.openSignupModal(() => {
          if (typeof window.handleEnhancedProductAddToCart === 'function') {
            window.handleEnhancedProductAddToCart(addToCartData);
          }
        });
      }
      return;
    }

    if (typeof window.handleEnhancedProductAddToCart === 'function') {
      window.handleEnhancedProductAddToCart(addToCartData)
        .then(() => {
          hideButtonLoading(dataAddToCartBtn);
          clearTimeout(fallbackTimeout);
        })
        .catch(() => {
          hideButtonLoading(dataAddToCartBtn);
          clearTimeout(fallbackTimeout);
          const errorBanner = enhancedSection.querySelector('[data-add-to-cart-error]');
          if (errorBanner) errorBanner.style.display = 'flex';
        });
    } else {
      // DMO handler not yet available — hide after short delay
      setTimeout(() => {
        hideButtonLoading(dataAddToCartBtn);
        clearTimeout(fallbackTimeout);
      }, 1000);
    }
  });
}

// ---------------------------------------------------------------------------
// App-only mode pricing adjustments
// ---------------------------------------------------------------------------

function checkAndAdjustAppOnlyPricing() {
  const enhancedSection = document.querySelector('.enhanced-product-section');
  if (!enhancedSection) return;
  if (enhancedSection.getAttribute('data-app-only-mode') !== 'true') return;

  const currentLocale = typeof getCurrentLocale === 'function' ? getCurrentLocale() : 'en-US';
  const langCode = currentLocale.split('-')[0];
  const translations = window.buttonTranslations || {};
  const trans =
    translations[currentLocale] ||
    Object.values(translations).find((_, i) =>
      Object.keys(translations)[i].startsWith(langCode + '-')
    ) ||
    translations['en-US'] ||
    {};

  const subscriptionTitle = enhancedSection.querySelector('.subscription-option.subscribe-save h3');
  if (subscriptionTitle) {
    subscriptionTitle.textContent = trans.getTheApp || 'Get the app';
  }

  // Hide the crossed-out original price in app-only mode
  const originalPriceEl = enhancedSection.querySelector(
    '.subscription-option.subscribe-save .original-price'
  );
  if (originalPriceEl) originalPriceEl.style.display = 'none';

  // Update button leading text node without re-rendering the price span
  const subscribeTextEl = enhancedSection.querySelector('.subscribe-text');
  if (subscribeTextEl) {
    const cartPriceEl = subscribeTextEl.querySelector('.cart-price');
    if (cartPriceEl) {
      const textNode = Array.from(subscribeTextEl.childNodes).find(
        (n) => n.nodeType === Node.TEXT_NODE
      );
      const newText = `${trans.subscribeNow || 'Get it now'} - `;
      if (textNode) {
        textNode.nodeValue = newText;
      } else {
        subscribeTextEl.insertBefore(document.createTextNode(newText), cartPriceEl);
      }
    }
  }
}

function observeAppOnlyMode() {
  const enhancedSection = document.querySelector('.enhanced-product-section');
  if (!enhancedSection) return;

  checkAndAdjustAppOnlyPricing();

  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      if (
        mutation.type === 'attributes' &&
        mutation.attributeName === 'data-app-only-mode'
      ) {
        checkAndAdjustAppOnlyPricing();
      }
    });
  });

  observer.observe(enhancedSection, {
    attributes: true,
    attributeFilter: ['data-app-only-mode'],
  });
}

// Initialize after a short delay to ensure DMO scripts have run
setTimeout(() => observeAppOnlyMode(), 500);
